import { cva, VariantProps } from 'class-variance-authority';
import { twMerge } from 'tailwind-merge';
import LabelAtom from './LabelAtom/labelAtom.tsx';
import Icon from '../Icon/icon.tsx';
import { IconNames, InputState } from '../../util/types.ts';
import React from 'react';

export interface InputWrapperProps {
  id: string;
  state?: InputState;
  disabled?: boolean;
  label: string;
  optionalityLabel?: string;
  helpText?: string[];
  message?: string;
  isTextarea?: boolean;
  className?: string;
  hasMarginBottom?: boolean;
  child: React.ReactNode;
}

const groupVariants = cva(
  ['preflight', 'group', 'ds-flex', 'ds-flex-col', 'ds-container'],
  {
    variants: {
      state: {
        default: ['[&>label]:focus-within:ds-text-acuity-blue'],
        error: ['[&>label]:focus-within:ds-text-danger-red-darkest'],
        success: ['[&>label]:focus-within:ds-text-success-green-darkest']
      },
      hasMarginBottom: {
        true: ''
      }
    },
    compoundVariants: [
      {
        state: 'default',
        hasMarginBottom: true,
        className: ['ds-mb-6']
      }
    ]
  }
);
interface GroupVariants extends VariantProps<typeof groupVariants> {}
export const groupStyles = (variants: GroupVariants) => twMerge(groupVariants(variants));

const textStateVariants = cva(['ds-form-label', '!ds-font-sans', 'ds-text-base'], {
  variants: {
    state: {
      default: ['ds-text-neutrals-black', 'ds-mt-2'],
      error: ['ds-text-danger-red', 'ds-mt-2'],
      success: ['ds-text-success-green-dark', 'ds-truncate', 'ds-text-sm', 'ds-mt-1']
    },
    hasMarginBottom: {
      true: ['ds-mb-6'],
      false: ['ds-mb-0']
    },
    disabled: {
      true: '!ds-text-neutrals-dark'
    }
  }
});

interface TextStateVariants extends VariantProps<typeof textStateVariants> {}
export const textStateStyles = (variants: TextStateVariants) =>
  twMerge(textStateVariants(variants));

const InputWrapper = ({
  id,
  state = 'default',
  disabled = false,
  label = '',
  optionalityLabel = '',
  helpText = [],
  message = '',
  child,
  isTextarea = false,
  className = '',
  hasMarginBottom = true
}: InputWrapperProps) => {
  let iconState = null;
  switch (state) {
    case 'error':
      iconState = {
        name: 'error' as IconNames,
        color: 'ds-text-danger-red'
      };
      break;
    case 'success':
      iconState = {
        name: 'checkCircle' as IconNames,
        color: 'ds-text-success-green'
      };
      break;
    default:
      break;
  }
  const labelledBy = label ? 'label_' + id : undefined;
  const describedBy = helpText && helpText.length > 0 ? 'helptext_' + id : undefined;

  return (
    <div className={twMerge(groupStyles({ state, hasMarginBottom }), className)}>
      {label && (
        <LabelAtom
          id={labelledBy}
          htmlFor={id}
          value={label}
          optionalityLabel={optionalityLabel}
          disabled={disabled}
        />
      )}
      {helpText && helpText.length > 1 && (
        <ul id={describedBy} className="ds-mt-2 ds-pb-1 ds-pl-6">
          {helpText?.map((text, index) => (
            <li className="ds-list-disc ds-text-neutrals-dark" key={index}>
              {text}
            </li>
          ))}
        </ul>
      )}
      {helpText && helpText.length === 1 && (
        <p id={describedBy} className="ds-mt-2 ds-pb-1 ds-text-neutrals-dark">
          {helpText}
        </p>
      )}
      <div className="ds-mt-2 ds-flex ds-gap-2">
        <div className={isTextarea ? '' : 'ds-w-[calc(100%-2rem)]'}>{child}</div>
        <div className="ds-pt-2">
          {iconState && (
            <Icon
              name={iconState.name}
              size="medium"
              className={iconState.color}
              role="graphics-symbol"
            />
          )}
        </div>
      </div>
      {iconState && (
        <p className={textStateStyles({ state, hasMarginBottom })}>{message}</p>
      )}
    </div>
  );
};

export default InputWrapper;
